import { useState, useEffect, useCallback, useMemo } from "react";
import { TrendingUp, Info, Target, HelpCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useToast } from "../../components/ui/toast";
import { formatCurrency } from "../../lib/utils";
import { Deal } from "../../types/deal";
import {
  ExitSettings,
  ExitStrategy as ExitStrategyType,
} from "../../types/exitStrategy";
import {
  createDefaultExitSettings,
  exitYearPresets,
  capRatePresets,
  ltvPresets,
} from "../../data/exitDefaults";
import {
  calculateSaleSummary,
  calculateRefinanceSummary,
} from "../../lib/exitCalc";
import { computeProjectIrrsWithHorizon } from "../../lib/finance/cashflow";
import { formatIRR } from "../../lib/finance/irr";
import type { Kpis } from "../../lib/kpis/defaults";
import { debounce } from "@/utils/useDebounce";
import { usePulseAnimation } from "@/helpers/animation-class";
import { getProjecById } from "@/lib/datasource/supabase";
import { patchProjectKpis } from "@/lib/datasource";
import { usePLData } from "@/hooks/usePLData";
import { calculatePLData } from "@/lib/pl/plCalculations";

type TemplateModeProps = {
  kpis?: Kpis | null;
  onChange?: (next: Kpis) => void;
  hideActions?: boolean;
};

type ProjectModeProps = {
  dealId?: string;
  onSaved?: () => void;
};

type ExitStrategyProps = ProjectModeProps & TemplateModeProps;

function parseDealToExitSettings(deal: Deal): ExitSettings {
  const defaults = createDefaultExitSettings();
  let exitData: ExitSettings;

  if (deal.assumptions?.exit) {
    const savedData = deal.assumptions.exit;
    exitData = {
      ...defaults,
      ...savedData,
      sale: { ...defaults.sale, ...(savedData.sale || {}) },
      refinance: {
        ...defaults.refinance,
        ...(savedData.refinance || {}),
      },
    };
  } else if (deal.kpis?.exit) {
    console.warn("[ExitStrategy] Миграция из 'kpis.exit'");
    const oldData = deal.kpis.exit as any;
    const saleData = oldData.sale || {};
    const refinanceData = oldData.refinance || {};
    exitData = {
      ...defaults,
      strategy: oldData.strategy || "SALE",
      sale: {
        ...defaults.sale,
        exitYear:
          saleData.exitYear ||
          oldData.holdPeriodYears ||
          defaults.sale.exitYear,
        exitCapRate:
          saleData.exitCapRate ||
          oldData.exitCapRate ||
          defaults.sale.exitCapRate,
        sellingCostsPct:
          saleData.sellingCostsPct ||
          oldData.sellingCostsPct ||
          defaults.sale.sellingCostsPct,
        exitNOIMultiple: oldData.exitNOIMultiple || 12,
        valuationMethod: oldData.valuationMethod || "capRate",
      },
      refinance: {
        ...defaults.refinance,
        refinanceYear:
          refinanceData.refinanceYear ||
          oldData.refinanceYear ||
          defaults.refinance.refinanceYear,
        ltvAtRefinance:
          refinanceData.ltvAtRefinance ||
          oldData.ltvAtRefinance ||
          defaults.refinance.ltvAtRefinance,
        refinanceCostsPct:
          refinanceData.refinanceCostsPct ||
          oldData.refinanceCostsPct ||
          defaults.refinance.refinanceCostsPct,
      },
    };
  } else {
    exitData = defaults;
  }
  return exitData;
}

function QuickButton({
  value,
  active,
  onClick,
  suffix = "",
  disabled = false,
}: {
  value: number;
  active: boolean;
  onClick: () => void;
  suffix?: string;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-1 rounded text-sm font-medium transition-all ${
        active
          ? "bg-primary-blue text-white shadow-sm"
          : disabled
            ? "bg-slate-100 text-slate-400 cursor-not-allowed"
            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
      }`}
    >
      {value}
      {suffix}
    </button>
  );
}

export default function ExitStrategy(props: ExitStrategyProps) {
  const { getAnimationClass, triggerMultiple } = usePulseAnimation();
  const { dealId, onSaved, kpis, onChange, hideActions } = props;

  const isTemplate = !!kpis && typeof onChange === "function";

  if (isTemplate) {
    const local = kpis!;

    const updateField = (path: string, value: any) => {
      const parts = path.split(".");
      if (parts[0] !== "exit") return;
      const exitField = parts[1];
      const exitDefaults = {
        holdPeriodYears: 5,
        valuationMethod: "capRate" as "capRate" | "multiple" | "dcf",
        exitCapRate: 7,
        exitNOIMultiple: 12,
        sellingCostsPct: 2,
        taxOnGainPct: 20,
        marketGrowthRate: 2,
        terminalGrowthRate: 2,
        discountRate: 10,
        probabilityOfSale: 100,
      };
      const newExit = {
        ...exitDefaults,
        ...(local.exit || {}),
        [exitField]: value,
      };
      onChange!({ ...local, exit: newExit as typeof local.exit });
    };

    const holdPeriodYears = (local.exit?.holdPeriodYears as number) || 5;
    const exitCapRate = (local.exit?.exitCapRate as number) || 7;
    const sellingCostsPct = (local.exit?.sellingCostsPct as number) || 2;
    const exitNOIMultiple = (local.exit?.exitNOIMultiple as number) || 12;
    const valuationMethod =
      (local.exit?.valuationMethod as string) || "capRate";

    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
        <div className="flex items-center space-x-2 mb-4">
          <Target className="h-5 w-5 text-slate-700" />
          <h3 className="text-lg font-semibold text-slate-900">
            Exit Strategy Defaults
          </h3>
        </div>
        <div className="space-y-6">
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <label className="block text-sm font-medium text-slate-700">
                Hold Period (Years)
              </label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-sm">
                      The expected holding period for the investment before
                      exit.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center space-x-3">
              <input
                type="number"
                value={holdPeriodYears || ""}
                onChange={(e) =>
                  updateField(
                    "exit.holdPeriodYears",
                    Number(e.target.value) || 0,
                  )
                }
                className="w-20 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                min="1"
                max="20"
              />
              <div className="flex space-x-2">
                {[3, 5, 7, 10].map((year) => (
                  <QuickButton
                    key={year}
                    value={year}
                    active={holdPeriodYears === year}
                    onClick={() => updateField("exit.holdPeriodYears", year)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-3">
              <label className="block text-sm font-medium text-slate-700">
                Exit Cap Rate (%)
              </label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-sm">
                      The capitalization rate used to value the property at
                      exit.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <input
                  type="number"
                  value={exitCapRate || ""}
                  onChange={(e) =>
                    updateField("exit.exitCapRate", Number(e.target.value) || 0)
                  }
                  className="w-20 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  min="0"
                  max="20"
                  step="0.1"
                />
                <span className="absolute right-3 top-2 text-slate-500 text-sm">
                  %
                </span>
              </div>
              <div className="flex space-x-2">
                {[5, 6, 7, 8].map((rate) => (
                  <QuickButton
                    key={rate}
                    value={rate}
                    active={exitCapRate === rate}
                    onClick={() => updateField("exit.exitCapRate", rate)}
                    suffix="%"
                  />
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-3">
              <label className="block text-sm font-medium text-slate-700">
                Selling Costs (%)
              </label>
            </div>
            <div className="relative">
              <input
                type="number"
                value={sellingCostsPct || ""}
                onChange={(e) =>
                  updateField(
                    "exit.sellingCostsPct",
                    Number(e.target.value) || 0,
                  )
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                min="0"
                max="10"
                step="0.1"
              />
              <span className="absolute right-3 top-2 text-slate-500 text-sm">
                %
              </span>
            </div>
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-3">
              <label className="block text-sm font-medium text-slate-700">
                Exit NOI Multiple
              </label>
            </div>
            <input
              type="number"
              value={exitNOIMultiple || ""}
              onChange={(e) =>
                updateField("exit.exitNOIMultiple", Number(e.target.value) || 0)
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              min="0"
              step="0.1"
            />
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-3">
              <label className="block text-sm font-medium text-slate-700">
                Valuation Method
              </label>
            </div>
            <select
              value={valuationMethod}
              onChange={(e) =>
                updateField("exit.valuationMethod", e.target.value)
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="capRate">Cap Rate</option>
              <option value="noiMultiple">NOI Multiple</option>
              <option value="dcf">Discounted Cash Flow</option>
            </select>
          </div>
        </div>
      </div>
    );
  }

  // ── Project mode ──────────────────────────────────────────────────────────

  const { toast } = useToast();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [exitSettings, setExitSettings] = useState<ExitSettings | null>(null);
  const [originalSettings, setOriginalSettings] = useState<ExitSettings | null>(
    null,
  );
  const [saveState, setSaveState] = useState<"idle" | "saving" | "success">(
    "idle",
  );

  useEffect(() => {
    if (!dealId) return;
    const loadDeal = async () => {
      const foundProject = await getProjecById(dealId);
      if (foundProject) {
        const foundDeal = foundProject as Deal;
        setDeal(foundDeal);
        const exitData = parseDealToExitSettings(foundDeal);
        setExitSettings(exitData);
        setOriginalSettings(exitData);
      }
    };
    loadDeal();
  }, [dealId]);

  const debouncedRecalculate = useCallback(
    debounce(() => {
      triggerMultiple(["returns", "summary"], 1000);
    }, 200),
    [triggerMultiple],
  );

  const handleStrategyChange = (strategy: ExitStrategyType) => {
    if (!exitSettings) return;
    setExitSettings({ ...exitSettings, strategy });
  };

  const handleSaleFieldChange = (
    field: keyof ExitSettings["sale"],
    value: number,
  ) => {
    if (!exitSettings) return;
    setExitSettings({
      ...exitSettings,
      sale: { ...exitSettings.sale, [field]: value },
    });
    debouncedRecalculate();
  };

  const handleRefinanceFieldChange = (
    field: keyof ExitSettings["refinance"],
    value: number,
  ) => {
    if (!exitSettings) return;
    setExitSettings({
      ...exitSettings,
      refinance: { ...exitSettings.refinance, [field]: value },
    });
    debouncedRecalculate();
  };

  const handleCancel = () => {
    if (originalSettings) {
      setExitSettings(originalSettings);
      toast.info("Changes discarded");
    }
  };

  // ── FIX 1: Патчим deal актуальными exitSettings чтобы usePLData
  // считал горизонт правильно (иначе deal.kpis.exit может быть устаревшим)
  const dealWithCurrentSettings = useMemo(() => {
    if (!deal || !exitSettings) return deal ?? undefined;
    return {
      ...deal,
      kpis: {
        ...deal.kpis,
        exit: exitSettings,
      },
    } as Deal;
  }, [deal, exitSettings]);

  const { allRows } = usePLData(dealWithCurrentSettings);

  // ── FIX 2: Считаем EBITDA напрямую через calculatePLData без horizon-обрезки,
  // чтобы год exitYear не был обнулён applyPLHorizon
  const ebitdaByYear = useMemo(() => {
    if (!dealWithCurrentSettings) return {};
    const rawRows = calculatePLData(dealWithCurrentSettings);
    const ebitdaRow = rawRows.find((r) => r.id === "ebitda");
    if (!ebitdaRow) return {};
    const result: Record<string, number> = {};
    ebitdaRow.years.forEach((y) => {
      result[`y${y.year}`] = y.total;
    });
    console.debug(
      "[ExitStrategy] ebitdaByYear:",
      result,
      "exitSettings.sale.exitYear:",
      exitSettings?.sale?.exitYear,
      "strategy:",
      exitSettings?.strategy,
    );
    return result;
  }, [dealWithCurrentSettings]);

  if (!deal || !exitSettings) {
    return <div>Loading...</div>;
  }

  const projectCost =
    deal.kpis?.investmentBudget?.grandTotal ||
    deal.kpis?.totalInvestment ||
    deal.kpis?.budget?.grandTotal ||
    0;

  const exitYear =
    exitSettings.strategy === "SALE"
      ? exitSettings.sale.exitYear
      : exitSettings.strategy === "REFINANCE"
        ? exitSettings.refinance.refinanceYear
        : 10;

  // ── FIX 3: убрана проверка > 0 для EBITDA в calculateSaleSummary
  // (см. exitCalc.ts — salePrice = referenceEBITDA / (exitCapRate / 100))
  const saleSummary = calculateSaleSummary(
    exitSettings,
    projectCost,
    deal,
    ebitdaByYear,
  );
  const refiSummary = calculateRefinanceSummary(
    exitSettings,
    projectCost,
    deal,
    ebitdaByYear,
  );

  let unleveredTerminalValue = 0;
  let leveredTerminalValue = 0;

  if (exitSettings.strategy === "SALE") {
    unleveredTerminalValue = saleSummary.netSaleProceeds;
    leveredTerminalValue = saleSummary.netSaleProceeds;
  } else if (exitSettings.strategy === "REFINANCE") {
    unleveredTerminalValue = 0;
    leveredTerminalValue = refiSummary.netCashOut;
  }

  const { unleveredIrr, leveredIrr } = computeProjectIrrsWithHorizon(
    deal,
    exitYear,
    {
      strategy: exitSettings.strategy,
      unleveredTerminalValue,
      leveredTerminalValue,
    },
  );

  const developmentProfit =
    exitSettings.strategy === "SALE"
      ? saleSummary.developmentProfit
      : exitSettings.strategy === "REFINANCE"
        ? refiSummary.netCashOut
        : 0;

  const handleSave = async () => {
    if (!deal || !exitSettings || !dealId) return;
    setSaveState("saving");
    try {
      const exitDataToSave = {
        ...exitSettings,
        estimatedReturns: {
          irrLevered: Math.round(leveredIrr * 100) / 100,
          irrUnlevered: Math.round(unleveredIrr * 100) / 100,
          developmentProfit,
        },
      };
      const result = await patchProjectKpis(dealId, { exit: exitDataToSave });
      if (result) {
        setOriginalSettings(exitSettings);
        setSaveState("success");
        toast.success("Exit strategy saved");
        if (onSaved) onSaved();
      } else {
        throw new Error("Failed to patch project KPIs");
      }
      setTimeout(() => setSaveState("idle"), 2000);
    } catch (error) {
      console.error("Error saving exit strategy:", error);
      setSaveState("idle");
      toast.error("Failed to save exit strategy");
    }
  };

  const assessReturn = (value: number, type: "irr" | "profit") => {
    if (typeof value !== "number" || isNaN(value))
      return { quality: "N/A", color: "text-slate-500" };
    if (type === "irr") {
      const pct = value * 100;
      if (pct < 8) return { quality: "Very Poor", color: "text-red-600" };
      if (pct < 12)
        return { quality: "Below Average", color: "text-orange-600" };
      if (pct < 18) return { quality: "Good", color: "text-green-600" };
      if (pct < 25) return { quality: "Excellent", color: "text-emerald-600" };
      return { quality: "Outstanding", color: "text-emerald-700" };
    } else {
      const profitMargin = projectCost > 0 ? (value / projectCost) * 100 : 0;
      if (profitMargin < 10)
        return { quality: "Very Poor", color: "text-red-600" };
      if (profitMargin < 20)
        return { quality: "Below Average", color: "text-orange-600" };
      if (profitMargin < 35)
        return { quality: "Good", color: "text-green-600" };
      if (profitMargin < 50)
        return { quality: "Excellent", color: "text-emerald-600" };
      return { quality: "Outstanding", color: "text-emerald-700" };
    }
  };

  const leveredAssessment = assessReturn(leveredIrr, "irr");
  const unleveredAssessment = assessReturn(unleveredIrr, "irr");
  const profitAssessment = assessReturn(developmentProfit, "profit");

  function StrategyCard({
    strategy,
    title,
    description,
    active,
    onSelect,
    disabled = false,
  }: {
    strategy: ExitStrategyType;
    title: string;
    description: string;
    active: boolean;
    onSelect: () => void;
    disabled?: boolean;
  }) {
    return (
      <button
        onClick={onSelect}
        disabled={disabled}
        className={`w-full text-left rounded-xl border p-4 transition-all duration-200 ${
          disabled
            ? "border-slate-200 bg-slate-50 cursor-not-allowed opacity-60"
            : active
              ? "border-[hsl(215_85%_45%)] ring-2 ring-[hsl(215_85%_45%)]/30 bg-[hsl(215_85%_96%)]"
              : "border-slate-200 hover:border-slate-300 bg-white hover:shadow-md"
        }`}
        aria-pressed={active}
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3
              className={`text-base font-semibold ${active ? "text-[hsl(215_85%_45%)]" : "text-slate-900"}`}
            >
              {title}
            </h3>
            {disabled && (
              <span className="text-xs px-2 py-1 bg-slate-200 text-slate-600 rounded">
                Coming Soon
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600">{description}</p>
        </div>
      </button>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2 flex items-center space-x-2">
          <Target className="h-5 w-5" />
          <span>Exit Assumptions</span>
        </h3>
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <p className="text-blue-800 text-sm font-medium">
            Configure your exit strategy to calculate IRR, development profit,
            and investment returns.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="space-y-4">
          <h3 className="text-base font-semibold text-slate-900">
            Exit Strategy
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StrategyCard
              strategy="HOLD_FOREVER"
              title="Hold Forever"
              description="Long-term cash flow strategy - P&L projection runs for all 10 years"
              active={exitSettings.strategy === "HOLD_FOREVER"}
              onSelect={() => handleStrategyChange("HOLD_FOREVER")}
            />
            <StrategyCard
              strategy="SALE"
              title="Sale"
              description="Exit via sale at specific year and cap rate"
              active={exitSettings.strategy === "SALE"}
              onSelect={() => handleStrategyChange("SALE")}
            />
            <StrategyCard
              strategy="REFINANCE"
              title="Refinance"
              description="Extract equity while maintaining ownership"
              active={exitSettings.strategy === "REFINANCE"}
              onSelect={() => handleStrategyChange("REFINANCE")}
              disabled={true}
            />
          </div>
        </div>
      </div>

      {exitSettings.strategy === "SALE" && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="space-y-6">
            <h3 className="text-base font-semibold text-slate-900">
              Sale Parameters
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <label className="block text-sm font-medium text-slate-700">
                    Exit Year
                  </label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-slate-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-sm">
                          The year when you plan to sell the property.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    value={exitSettings.sale.exitYear || ""}
                    onChange={(e) =>
                      handleSaleFieldChange(
                        "exitYear",
                        Number(e.target.value) || 0,
                      )
                    }
                    className="w-20 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    min="1"
                    max="20"
                  />
                  <div className="flex space-x-2">
                    {exitYearPresets.map((year) => (
                      <QuickButton
                        key={year}
                        value={year}
                        active={exitSettings.sale.exitYear === year}
                        onClick={() => handleSaleFieldChange("exitYear", year)}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <label className="block text-sm font-medium text-slate-700">
                    Exit Cap Rate
                  </label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-slate-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="max-w-xs text-sm space-y-2">
                          <p className="font-semibold">
                            Exit Cap Rate - Simple Explanation
                          </p>
                          <p>
                            Lower cap rates mean the hotel is worth more money.
                          </p>
                          <div className="border-t pt-2 space-y-1">
                            <p className="font-medium">Typical Benchmarks:</p>
                            <p>• 4-5%: Prime locations</p>
                            <p>• 5-6%: Good locations</p>
                            <p>• 6-7%: Average market</p>
                            <p>• 8%+: Higher risk</p>
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <input
                      type="number"
                      value={exitSettings.sale.exitCapRate || ""}
                      onChange={(e) =>
                        handleSaleFieldChange(
                          "exitCapRate",
                          Number(e.target.value) || 0,
                        )
                      }
                      className="w-20 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      min="0"
                      max="20"
                      step="0.1"
                    />
                    <span className="absolute right-3 top-2 text-slate-500 text-sm">
                      %
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    {capRatePresets.map((rate) => (
                      <QuickButton
                        key={rate}
                        value={rate}
                        active={exitSettings.sale.exitCapRate === rate}
                        onClick={() =>
                          handleSaleFieldChange("exitCapRate", rate)
                        }
                        suffix="%"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center space-x-2 mb-3">
                <label className="block text-sm font-medium text-slate-700">
                  Selling Costs
                </label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-slate-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs text-sm">
                        Transaction costs including broker fees, legal fees, and
                        transfer taxes.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <input
                    type="number"
                    value={exitSettings.sale.sellingCostsPct || ""}
                    onChange={(e) =>
                      handleSaleFieldChange(
                        "sellingCostsPct",
                        Number(e.target.value) || 0,
                      )
                    }
                    className="w-20 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    min="0"
                    max="10"
                    step="0.1"
                  />
                  <span className="absolute right-3 top-2 text-slate-500 text-sm">
                    %
                  </span>
                </div>
                <div className="flex space-x-2">
                  {[2, 3, 4, 5].map((pct) => (
                    <QuickButton
                      key={pct}
                      value={pct}
                      active={exitSettings.sale.sellingCostsPct === pct}
                      onClick={() =>
                        handleSaleFieldChange("sellingCostsPct", pct)
                      }
                      suffix="%"
                    />
                  ))}
                </div>
              </div>
            </div>

            <div
              className={`border border-slate-200 rounded-lg p-4 bg-slate-50 ${getAnimationClass("summary")}`}
            >
              <h4 className="font-medium text-slate-900 mb-3">
                Estimated Sale Summary
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Reference Year EBITDA:</span>
                  <span className="font-medium text-slate-900">
                    {formatCurrency(saleSummary.referenceEBITDA, deal.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Exit Cap Rate:</span>
                  <span className="font-medium text-slate-900">
                    {saleSummary.exitCapRate}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Estimated Sale Price:</span>
                  <span className="font-medium text-slate-900">
                    {formatCurrency(
                      saleSummary.estimatedSalePrice,
                      deal.currency,
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Selling Costs:</span>
                  <span className="font-medium text-slate-900">
                    {formatCurrency(saleSummary.sellingCosts, deal.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Net Sale Proceeds:</span>
                  <span className="font-medium text-slate-900">
                    {formatCurrency(saleSummary.netSaleProceeds, deal.currency)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-2">
                  <span className="text-slate-600">Less: Total CapEx:</span>
                  <span className="font-medium text-slate-900">
                    -{formatCurrency(saleSummary.totalCapEx, deal.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-700 font-medium">
                    Development Profit:
                  </span>
                  <span
                    className={`font-semibold ${saleSummary.developmentProfit >= 0 ? "text-green-600" : "text-red-600"}`}
                  >
                    {formatCurrency(
                      saleSummary.developmentProfit,
                      deal.currency,
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {exitSettings.strategy === "REFINANCE" && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="space-y-6">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-4">
              <div className="flex items-center space-x-2">
                <Info className="h-5 w-5 text-amber-600" />
                <p className="text-amber-800 font-medium text-sm">
                  Refinance functionality is coming soon.
                </p>
              </div>
            </div>
            <h3 className="text-base font-semibold text-slate-900">
              Refinance Parameters (Coming Soon)
            </h3>
            <div className="space-y-4 opacity-50 pointer-events-none">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Refinance Year
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="number"
                      value={exitSettings.refinance.refinanceYear || ""}
                      className="w-20 px-3 py-2 border border-slate-300 rounded-md bg-slate-100"
                      disabled
                    />
                    <div className="flex space-x-2">
                      {exitYearPresets.map((year) => (
                        <QuickButton
                          key={year}
                          value={year}
                          active={exitSettings.refinance.refinanceYear === year}
                          onClick={() => {}}
                          disabled={true}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    LTV at Refinance
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="number"
                      value={exitSettings.refinance.ltvAtRefinance || ""}
                      className="w-20 px-3 py-2 border border-slate-300 rounded-md bg-slate-100"
                      disabled
                    />
                    <div className="flex space-x-2">
                      {ltvPresets.map((ltv) => (
                        <QuickButton
                          key={ltv}
                          value={ltv}
                          active={exitSettings.refinance.ltvAtRefinance === ltv}
                          onClick={() => {}}
                          disabled={true}
                          suffix="%"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <h4 className="font-medium text-slate-900 mb-3">
                  Estimated Refinance Summary
                </h4>
                <p className="text-slate-500 text-sm italic">
                  Refinance calculations will be available soon
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {exitSettings.strategy === "HOLD_FOREVER" && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-slate-900">
              Hold Forever Strategy
            </h3>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-center space-x-2">
                <Info className="h-5 w-5 text-blue-600" />
                <p className="text-blue-800 font-medium text-sm">
                  Long-term hold strategy - P&L projection runs for all 10
                  years.
                </p>
              </div>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 mt-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-green-800">
                    Strategy Features:
                  </p>
                  <ul className="mt-2 space-y-1 text-green-700">
                    <li>• Full 10-year P&L projection</li>
                    <li>• Continuous cash flow analysis</li>
                    <li>• Long-term asset appreciation</li>
                    <li>• No exit event</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-green-800">Best For:</p>
                  <ul className="mt-2 space-y-1 text-green-700">
                    <li>• Income-focused investors</li>
                    <li>• Long-term portfolio holdings</li>
                    <li>• Assets with strong cash flow</li>
                    <li>• Tax-efficient holding</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {exitSettings.strategy !== "HOLD_FOREVER" &&
        exitSettings.strategy !== "REFINANCE" && (
          <div
            className={`rounded-xl border border-slate-200 bg-white p-6 shadow-sm ${getAnimationClass("returns")}`}
          >
            <div className="space-y-6">
              <h3 className="text-base font-semibold text-slate-900">
                Expected Returns Summary
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-medium text-emerald-700">
                      IRR (Levered)
                    </span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-emerald-500" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="max-w-xs text-sm space-y-2">
                            <p className="font-semibold">IRR (Levered)</p>
                            <p>
                              Annual return % when using debt to finance part of
                              the project.
                            </p>
                            <div className="border-t pt-2 space-y-1">
                              <p>• 8-12%: Below Average</p>
                              <p>• 12-18%: Good</p>
                              <p>• 18-25%: Excellent</p>
                              <p>• 25%+: Outstanding</p>
                            </div>
                            <p
                              className={`font-semibold ${leveredAssessment.color}`}
                            >
                              Your {formatIRR(leveredIrr)} is:{" "}
                              {leveredAssessment.quality}
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="text-2xl font-bold text-emerald-900">
                    {formatIRR(leveredIrr)}
                  </div>
                </div>

                <div className="rounded-xl bg-purple-50 border border-purple-200 p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-medium text-purple-700">
                      IRR (Unlevered)
                    </span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-purple-500" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="max-w-xs text-sm space-y-2">
                            <p className="font-semibold">IRR (Unlevered)</p>
                            <p>
                              Annual return % if you paid 100% cash (no loans).
                            </p>
                            <div className="border-t pt-2 space-y-1">
                              <p>• 6-10%: Below Average</p>
                              <p>• 10-15%: Good</p>
                              <p>• 15-20%: Excellent</p>
                              <p>• 20%+: Outstanding</p>
                            </div>
                            <p
                              className={`font-semibold ${unleveredAssessment.color}`}
                            >
                              Your {formatIRR(unleveredIrr)} is:{" "}
                              {unleveredAssessment.quality}
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="text-2xl font-bold text-purple-900">
                    {formatIRR(unleveredIrr)}
                  </div>
                </div>

                <div className="rounded-xl bg-blue-50 border border-blue-200 p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-medium text-blue-700">
                      Development Profit
                    </span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-blue-500" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="max-w-xs text-sm space-y-2">
                            <p className="font-semibold">Development Profit</p>
                            <p>
                              Total money you make: what you get back minus what
                              you put in.
                            </p>
                            <div className="border-t pt-2 space-y-1">
                              <p>• 10-20%: Below Average</p>
                              <p>• 20-35%: Good</p>
                              <p>• 35-50%: Excellent</p>
                              <p>• 50%+: Outstanding</p>
                            </div>
                            <p
                              className={`font-semibold ${profitAssessment.color}`}
                            >
                              Your margin is: {profitAssessment.quality}
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="text-2xl font-bold text-blue-900">
                    {formatCurrency(developmentProfit, deal.currency)}
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-center space-x-2">
                  <Info className="h-5 w-5 text-amber-600" />
                  <p className="text-amber-800 text-sm font-medium">
                    IRR calculations update dynamically based on P&L data.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

      {!hideActions && (
        <div className="flex items-center space-x-3 pt-4 justify-end">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={saveState === "saving"}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saveState === "saving"}
            className={`${saveState === "success" ? "bg-green-600 hover:bg-green-700" : "bg-[hsl(215_85%_45%)] hover:bg-[hsl(215_85%_40%)]"} text-white flex items-center space-x-2`}
          >
            {saveState === "saving" && (
              <TrendingUp className="h-4 w-4 animate-spin" />
            )}
            {saveState === "success" && <TrendingUp className="h-4 w-4" />}
            <span>
              {saveState === "saving" && "Saving..."}
              {saveState === "success" && "Saved ✓"}
              {saveState === "idle" && "Save Exit Assumptions"}
            </span>
          </Button>
        </div>
      )}

      <div className="text-center">
        <p className="text-xs text-slate-500">
          Recalculated • {new Date().toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
}
